import AppStats from "components/appStats";
import Layout from "layout";
import React, { useState, useMemo } from "react";
import { Link, NavLink } from "react-router-dom";
import { useGetTransactionsList } from "utils/api/user.api";
import { type UserTransaction } from "utils/types/user.type";

export default function CompletedTransactions() {
  const [searchValue, setSearchValue] = useState("");
  const { data: transactionsResponse, isLoading } = useGetTransactionsList({
    status: "completed",
    limit: 100,
  });

  const completedTrans = transactionsResponse?.data ?? [];

  const filteredTransactions = useMemo(() => {
    if (!searchValue) return completedTrans;
    const searchLower = searchValue.toLowerCase();
    return completedTrans.filter((transaction: UserTransaction) =>
      transaction.user?.email?.toLowerCase().includes(searchLower) ||
      transaction.type?.toLowerCase().includes(searchLower) ||
      transaction.coin?.symbol?.toLowerCase().includes(searchLower) ||
      transaction.id?.toLowerCase().includes(searchLower)
    );
  }, [completedTrans, searchValue]);
  return (
    <Layout>
      <div className="dashboard-main">
        <div className="main-wrapper max-w-5xl p-6">
          <AppStats />
          <ul className="flex flex-wrap items-center gap-3 mb-4">
            <li>
              <NavLink
                to="/transaction-history"
                className="text-black-900/20 aria-[current=page]:text-black-900 text-xl"
              >
                Transaction History
              </NavLink>
            </li>
          </ul>
          {/* <form action="#" className="flex items-center gap-6 mb-8"> */}
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Search by date, email address or transaction type"
            className="w-full text-black-700 text-sm rounded-lg border border-black-800 focus:outline-0 py-3.5 px-5"
          />
          {/* <button className="hover:bg-cyan-800 bg-cyan-300 border border-cyan-300 rounded-lg text-black-700 text-base font-bold transition px-8 lg:px-16 py-3">
              Search
            </button>
          </form> */}

          <div className="table-wrapper max-h-[38rem] overflow-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="rounded-l-lg border border-black-800/30 border-r-0 py-5 px-5 text-left">
                    Date
                  </th>
                  <th className="py-5 px-5 border-y border-black-800/30 text-left">
                    Details
                  </th>
                  <th className="py-5 px-5 border-y border-black-800/30 text-center">
                    Amount
                  </th>
                  <th className="py-5 px-5 border-y border-black-800/30 text-center">
                    Fiat Amount
                  </th>
                  <th className="rounded-r-lg border border-black-800/30 border-l-0 py-5 px-16 text-left">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.length > 0 ? (
                  filteredTransactions.map((transaction) => (
                    <TableRow key={transaction.id} transaction={transaction} />
                  ))
                ) : !isLoading ? (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-black-700">
                      No Results Found
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function TableRow({ transaction }: { transaction: UserTransaction }) {
  const [isEdit, setIsEdit] = useState(false);
  return (
    <>
      <tr
        className={` ${
          transaction?.type === "Withdraw"
            ? "bg-[#FFDEEA]"
            : transaction?.type === "Swap"
            ? "bg-[#F3F3F3]"
            : transaction?.type === "Buy" || transaction?.type === "Sell"
            ? "bg-purple-100"
            : "bg-cyan-300"
        }`}
      >
        <td className="rounded-l-lg px-2 border border-black-800/30 border-r-0 py-5">
          <p className="text-black-900 text-xs ml-3">
            {new Date(transaction?.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </p>
        </td>
        <td className="py-5 px-2 border-y border-black-800/30">
          <p className="text-black-900 text-xs font-semibold">
            <b>{transaction?.type}</b> from {transaction?.user?.email}
          </p>
          {transaction?.user?.fullname && (
            <p className="text-black-700 text-xs mt-1">
              {transaction.user.fullname}
            </p>
          )}
        </td>
        <td className="py-5 px-2 border-y border-black-800/30">
          <p className="text-black-900 text-sm text-center">
            {Number(transaction?.amount || 0).toLocaleString(undefined, {
              maximumFractionDigits: 6,
            })}{" "}
            {transaction?.coin?.symbol}
          </p>
        </td>
        <td className="py-5 px-2 border-y border-black-800/30">
          <p className="text-black-900 text-sm text-center">
            ${Number(transaction?.amountInUsd || 0).toLocaleString(undefined, {
              maximumFractionDigits: 2,
            })}
          </p>
        </td>
        <td className="rounded-r-lg px-2 border border-black-800/30 border-l-0 text-center py-5">
          <button
            onClick={() => setIsEdit(!isEdit)}
            type="button"
            className={`${
              transaction?.type === "Withdraw"
                ? "bg-[#FF689F] text-white"
                : transaction?.type === "Swap"
                ? "bg-[#D4D4D4] text-black"
                : "bg-cyan-800 text-white"
            } text-xs font-semibold rounded-lg px-5 py-2`}
          >
            {isEdit ? "Collapse" : "See Details"}
          </button>
        </td>
      </tr>
      {isEdit && (
        <tr
          className={`${
            transaction?.type === "Withdraw"
              ? "bg-[#FFDEEA]"
              : transaction?.type === "Swap"
              ? "bg-[#F3F3F3]"
              : "bg-cyan-300"
          }`}
        >
          <td
            colSpan={5}
            className="border border-black-800/30 border-t-0 rounded-b-lg p-6"
          >
            <div className="bg-white/50 rounded-lg p-4">
              <h4 className="text-sm font-bold text-black-800 mb-4">
                Transaction Details
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <DetailItem label="Transaction ID" value={transaction.id} />
                {transaction.fromAddress && (
                  <DetailItem label="From Address" value={transaction.fromAddress} />
                )}
                {transaction.toAddress && (
                  <DetailItem label="To Address" value={transaction.toAddress} />
                )}
                {transaction.trxHash && (
                  <DetailItem label="Transaction Hash" value={transaction.trxHash} />
                )}
                <DetailItem
                  label="Network"
                  value={transaction.coin?.network?.name || transaction.coin?.name || "N/A"}
                />
                <DetailItem
                  label="Amount (USD)"
                  value={`$${Number(transaction.amountInUsd || 0).toLocaleString(undefined, {
                    maximumFractionDigits: 2,
                  })}`}
                />
                <DetailItem label="Wallet ID" value={transaction.walletId} />
                <DetailItem label="Coin ID" value={transaction.coinId} />
                <div className="col-span-full">
                  <Link
                    to={`/users?search=${transaction?.user?.email}`}
                    className="text-black-900 text-xs leading-relaxed font-semibold hover:underline"
                  >
                    Check user in directory →
                  </Link>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-black-700 font-medium mb-1">{label}</p>
      <p className="text-sm text-black-900 font-semibold break-all">{value}</p>
    </div>
  );
}
